import { useEffect, useState } from "react";
import api from "../lib/api";
import { AlertTriangle, Archive, Loader2, Trash2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";

/**
 * Delete confirmation with impact preview.
 * Props: open, onOpenChange, project, onArchive (async), onDelete (async), onCancel
 */
export default function DeleteProjectDialog({ open, onOpenChange, project, onArchive, onDelete }) {
  const { canDelete } = useAuth();
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && project) {
      setImpact(null);
      api.get(`/projects/${project.id}/impact`).then((r) => setImpact(r.data));
    }
  }, [open, project]);

  const doArchive = async () => {
    setBusy(true);
    try {
      await onArchive();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await onDelete();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg rounded-md" data-testid="delete-project-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-sm bg-red-100 text-red-700">
              <Trash2 size={20} />
            </div>
            <div>
              <AlertDialogTitle className="font-display text-2xl font-black tracking-tight text-slate-900">
                Delete this project?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600 mt-1">
                Are you sure you want to delete <span className="font-semibold text-slate-900">{project?.name}</span>?
                This action cannot be undone.
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        {impact === null ? (
          <div className="text-sm text-slate-500 font-mono py-4">Checking project history…</div>
        ) : impact.has_history || impact.has_onsite_inventory ? (
          <div className="my-2 space-y-3">
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md" data-testid="delete-warning">
              <div className="flex gap-3">
                <AlertTriangle className="text-amber-700 shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-amber-900">
                  <div className="font-bold">Inventory history is attached</div>
                  <p className="mt-1">
                    This project has <span className="font-mono font-semibold">{impact.transaction_count}</span> receiving/transfer
                    transactions attached to it. Deleting this project may remove related records.
                  </p>
                </div>
              </div>
            </div>

            {impact.has_onsite_inventory && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-md" data-testid="onsite-warning">
                <div className="flex gap-3">
                  <AlertTriangle className="text-red-700 shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-red-900">
                    <div className="font-bold">Physical stock is on-site at this project</div>
                    <p className="mt-1">
                      Inventory was moved from the warehouse to this project. Deletion will
                      <span className="font-semibold"> not </span>
                      change your physical warehouse quantity. Consider returning items to the warehouse first.
                    </p>
                    <ul className="mt-2 space-y-1 font-mono text-xs">
                      {impact.onsite_inventory.map((i) => (
                        <li key={i._id}>
                          • {i.item_name} — <span className="font-semibold">{i.net}</span> {i.sku ? `(${i.sku})` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="my-2 text-sm text-slate-600">
            No inventory history is attached to this project. Deletion is safe.
          </div>
        )}

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="delete-cancel-btn"
            className="rounded-sm"
          >
            <X size={16} className="mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={doArchive}
            disabled={busy}
            data-testid="delete-archive-instead-btn"
            className="bg-amber-400 hover:bg-amber-500 text-amber-950 rounded-sm font-semibold"
          >
            <Archive size={16} className="mr-1.5" /> Archive Instead
          </Button>
          {canDelete ? (
            <Button
              onClick={doDelete}
              disabled={busy}
              data-testid="delete-permanent-btn"
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-semibold"
            >
              {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Trash2 size={16} className="mr-1.5" />}
              Delete Permanently
            </Button>
          ) : (
            <div className="text-xs text-slate-500 italic px-2">
              Only Admin or Installation Manager can permanently delete.
            </div>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
